import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { ShoppingCart, Plus, Minus, X, Search, Package, MessageCircle, Send, LogIn, SlidersHorizontal, ShieldCheck, Truck, Check, ChevronDown } from 'lucide-react';

const THEMES = {
  inventory: { name: 'FCP', primary: '#0B4F9E', accent: '#FF6600', text: '#111827', muted: '#6B7280', bg: '#F4F6F9', surface: '#FFFFFF', border: '#E4E8EF', headerBg: '#0B2A4A', headerText: '#FFFFFF', badge: 'Compatibilidad garantizada', brandImg: '' },
  fya: { name: 'FyA', primary: '#C4161C', accent: '#1D4ED8', text: '#141414', muted: '#5B6067', bg: '#EEF0F3', surface: '#FFFFFF', border: '#D8DCE3', headerBg: '#0E0E10', headerText: '#FFFFFF', badge: 'Ajuste garantizado', brandImg: '/brand/fya_favicon.jpg', silver: '#C7CBD1' },
  salespro: { name: 'SalesPRO', primary: '#00796B', accent: '#F59E0B', text: '#0F172A', muted: '#64748B', bg: '#F0FBF7', surface: '#FFFFFF', border: '#D8E8E2', headerBg: '#022C22', headerText: '#FFFFFF', badge: 'Producto verificado', brandImg: '' },
};
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export default function Storefront() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waking, setWaking] = useState(false);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [activeCat, setActiveCat] = useState('');
  const [onlyStock, setOnlyStock] = useState(false);
  const [cart, setCart] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [detail, setDetail] = useState(null);
  const [customer, setCustomer] = useState({ name: '', phone: '', note: '' });
  const [sending, setSending] = useState(false);
  const cartKey = `store_cart_${slug}`;
  const isLogged = authService.isAuthenticated();
  const loggedUser = authService.getUser();

  useEffect(() => {
    let cancelled = false;
    const fetchStore = async (attempt = 0) => {
      try {
        const res = await api.getStore(slug);
        if (!cancelled) { setData(res.data); setErr(''); setWaking(false); setLoading(false); }
      } catch (e) {
        const status = e.response?.status;
        const coldStart = !e.response || status === 502 || status === 503 || status === 504;
        if (coldStart && attempt < 10 && !cancelled) {
          setWaking(true);
          setTimeout(() => fetchStore(attempt + 1), 4000);
        } else if (!cancelled) {
          setErr(e.response?.data?.detail || 'Tienda no encontrada'); setWaking(false); setLoading(false);
        }
      }
    };
    fetchStore();
    try { setCart(JSON.parse(localStorage.getItem(cartKey) || '{}')); } catch { /* noop */ }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  useEffect(() => { localStorage.setItem(cartKey, JSON.stringify(cart)); }, [cart, cartKey]);

  const t = THEMES[data?.config?.theme] || THEMES.salespro;
  const logo = (data?.company?.logo_url) ? (data.company.logo_url.startsWith('http') ? data.company.logo_url : api.publicFileUrl(data.company.logo_url)) : '';
  const initials = (data?.company?.name || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  const Brand = ({ cls }) => logo
    ? <img src={logo} alt="" className={cls + ' object-cover'} onError={(e) => { e.target.style.display = 'none'; }} />
    : <span className={cls + ' flex items-center justify-center font-extrabold'} style={{ background: t.primary, color: '#fff' }}>{initials}</span>;

  // Favicon dinámico por tienda
  useEffect(() => {
    if (!logo) return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    const prev = link.href; link.href = logo;
    return () => { link.href = prev; };
  }, [logo]);

  const products = useMemo(() => data?.products || [], [data]);
  const categories = data?.categories || [];
  const catName = (id) => categories.find(c => c.category_id === id)?.name || 'General';

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return products.filter(p => {
      if (activeCat && p.category_id !== activeCat) return false;
      if (onlyStock && p.item_type !== 'servicio' && (p.stock || 0) <= 0) return false;
      if (!qq) return true;
      return (p.name || '').toLowerCase().includes(qq) || (p.code || '').toLowerCase().includes(qq) || (p.marca || '').toLowerCase().includes(qq);
    });
  }, [products, q, activeCat, onlyStock]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(p => { const k = p.category_id || '__none'; (map[k] = map[k] || { name: catName(p.category_id), items: [] }).items.push(p); });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered]);

  const addToCart = (p) => { setCart(c => ({ ...c, [p.product_id]: { ...p, quantity: (c[p.product_id]?.quantity || 0) + 1 } })); setCartOpen(true); setDetail(null); };
  const setQty = (id, qty) => setCart(c => { const n = { ...c }; if (qty <= 0) delete n[id]; else n[id] = { ...n[id], quantity: qty }; return n; });
  const cartItems = Object.values(cart);
  const cartCount = cartItems.reduce((s, i) => s + i.quantity, 0);
  const cartTotal = cartItems.reduce((s, i) => s + i.price * i.quantity, 0);

  const waNumber = (data?.config?.whatsapp_number || data?.company?.phone || '').replace(/[^0-9]/g, '');
  const orderWhatsapp = () => {
    if (!cartItems.length) return toast.error('Tu carrito está vacío');
    if (!waNumber) return toast.error('Esta tienda no tiene WhatsApp configurado');
    const who = isLogged && loggedUser ? `Cliente: ${loggedUser.full_name || loggedUser.email} (registrado)` : `Enviado desde: ${customer.phone || 'sin teléfono'}`;
    const lines = cartItems.map(i => `• ${i.quantity} x ${i.name} — ${money(i.price * i.quantity)}`);
    const msg = encodeURIComponent(`*Pedido ${data.company.name}*\n${lines.join('\n')}\n\n*Total: ${money(cartTotal)}*\n\n${who}${customer.note ? `\nNota: ${customer.note}` : ''}`);
    window.open(`https://wa.me/${waNumber}?text=${msg}`, '_blank');
  };
  const sendOrder = async () => {
    if (!isLogged) { toast.error('Inicia sesión o regístrate para enviar tu pedido'); return navigate('/login'); }
    if (!cartItems.length) return toast.error('Tu carrito está vacío');
    setSending(true);
    try {
      const res = await api.createStoreOrder(slug, { items: cartItems.map(i => ({ product_id: i.product_id, quantity: i.quantity })), customer_name: (loggedUser?.full_name || customer.name || 'Cliente'), customer_phone: customer.phone, note: customer.note });
      toast.success(`Pedido enviado: ${res.data.invoice_number}`);
      setCart({}); setCartOpen(false); setCustomer({ name: '', phone: '', note: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo enviar el pedido'); } finally { setSending(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white text-gray-700 gap-4" data-testid="store-loading">
      <div style={{ width: 42, height: 42, border: '3px solid #e5e7eb', borderTopColor: '#022C22', borderRadius: '50%', animation: 'spin 0.9s linear infinite' }} />
      <div className="text-center px-6">
        <div className="font-medium">Cargando tienda…</div>
        {waking && <div className="text-sm text-gray-500 mt-1">El servidor está despertando, puede tardar ~30s la primera vez.</div>}
      </div>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
  if (err) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white text-gray-700 gap-3 px-6 text-center" data-testid="store-error">
      <div className="text-lg font-medium">{err}</div>
      <button onClick={() => { setErr(''); setLoading(true); window.location.reload(); }} className="text-sm px-4 py-2 rounded-lg text-white" style={{ background: '#022C22' }} data-testid="store-retry">Reintentar</button>
    </div>
  );

  const catList = [{ category_id: '', name: 'Todos' }, ...categories];
  const featured = products.filter(p => (p.photos && p.photos[0])).slice(0, 6);

  const ProductCard = ({ p }) => {
    const noStock = p.item_type !== 'servicio' && (p.stock || 0) <= 0;
    const img = p.photos && p.photos[0] ? (p.photos[0].startsWith('http') ? p.photos[0] : api.publicFileUrl(p.photos[0])) : null;
    return (
      <div className="st-card rounded-xl overflow-hidden flex flex-col h-full" style={{ background: t.surface, border: `1px solid ${t.border}` }} data-testid={`store-product-${p.product_id}`}>
        <button onClick={() => setDetail(p)} className="relative block aspect-square w-full bg-gray-50 overflow-hidden" data-testid={`store-view-${p.product_id}`}>
          {img ? <img src={img} alt={p.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} /> : <span className="absolute inset-0 flex items-center justify-center"><Package size={40} className="text-gray-300" /></span>}
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ background: noStock ? '#4B5563' : t.primary }}>{noStock ? 'Agotado' : <><Check size={11} /> {t.badge}</>}</span>
        </button>
        <div className="p-3 flex flex-col flex-1">
          <span className="text-[10px] uppercase tracking-wide font-semibold h-4 truncate" style={{ color: t.muted }}>{p.marca || ' '}</span>
          <button onClick={() => setDetail(p)} className="text-left font-semibold text-sm leading-snug line-clamp-2 h-10" style={{ color: t.text }}>{p.name}</button>
          <div className="mt-auto pt-2 flex items-end justify-between">
            <span className="font-extrabold text-lg" style={{ color: t.primary }}>{money(p.price)}</span>
            {p.item_type !== 'servicio' && <span className="text-[11px]" style={{ color: noStock ? '#DC2626' : t.muted }}>{noStock ? 'Sin stock' : `Stock ${p.stock}`}</span>}
          </div>
          <button onClick={() => addToCart(p)} disabled={noStock} className="mt-2 w-full py-2 rounded-lg text-sm font-bold text-white disabled:opacity-40 flex items-center justify-center gap-1" style={{ background: t.accent }} data-testid={`store-add-${p.product_id}`}><Plus size={15} /> Agregar</button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen pb-20 lg:pb-0" style={{ background: t.bg, color: t.text }} data-testid="storefront">
      <style>{`
        @keyframes st-pop{from{transform:scale(.9);opacity:.4}to{transform:scale(1);opacity:1}}
        .st-pop{animation:st-pop .18s ease}
        .st-card{transition:transform .2s,box-shadow .2s}
        .st-card:hover{transform:translateY(-4px);box-shadow:0 14px 30px rgba(0,0,0,.10)}
      `}</style>

      {/* HEADER */}
      <header className="sticky top-0 z-40" style={{ background: t.headerBg, color: t.headerText }} data-testid="store-header">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-3">
          <div className="flex items-center gap-2 min-w-0 shrink-0">
            <Brand cls="h-10 w-10 rounded-full" />
            <span className="font-extrabold text-lg tracking-tight truncate max-w-[38vw] sm:max-w-none">{data.company.name}</span>
          </div>
          <div className="relative flex-1 max-w-xl mx-auto">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar pieza, código o marca…" className="w-full pl-10 pr-3 py-2.5 rounded-full text-sm text-gray-900 bg-white outline-none" data-testid="store-search" />
          </div>
          <button onClick={() => navigate('/login')} className="hidden sm:inline-flex items-center gap-1 text-sm font-medium opacity-90 hover:opacity-100" data-testid="store-login"><LogIn size={16} /> {isLogged ? 'Mi cuenta' : 'Ingresar'}</button>
          <button onClick={() => setCartOpen(true)} className="relative inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-white shrink-0" style={{ background: t.accent }} data-testid="store-cart-button">
            <ShoppingCart size={17} /><span className="hidden sm:inline">Carrito</span>
            {cartCount > 0 && <span className="st-pop absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[11px] flex items-center justify-center rounded-full bg-black text-white" data-testid="store-cart-count">{cartCount}</span>}
          </button>
        </div>
      </header>

      <div style={{ background: t.primary }} className="text-white text-center text-xs sm:text-sm font-medium py-2 px-4" data-testid="store-promo"><Truck size={14} className="inline mr-1 -mt-0.5" /> {data.config.hero_subtitle || 'Envío rápido y atención por WhatsApp'}</div>

      {/* FEATURED / PROPAGANDA */}
      <section className="relative overflow-hidden" style={{ background: t.headerBg, color: t.headerText }} data-testid="store-hero">
        <div className="max-w-7xl mx-auto px-4 py-8 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
          <div className="flex items-center gap-4">
            {logo ? <img src={logo} alt="" className="h-20 w-20 rounded-2xl object-cover shadow-lg" style={{ boxShadow: `0 0 30px ${t.primary}66` }} onError={(e) => { e.target.style.display = 'none'; }} /> : <Brand cls="h-20 w-20 rounded-2xl text-2xl" />}
            <div>
              <h1 className="font-extrabold tracking-tight text-3xl sm:text-4xl">{data.config.hero_title || data.company.name}</h1>
              <p className="mt-1 text-sm opacity-80">{products.length} productos · {categories.length} categorías</p>
              <span className="mt-2 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold" style={{ background: t.primary }}><ShieldCheck size={13} /> {t.badge}</span>
            </div>
          </div>
          {featured.length > 0 && (
            <div className="hidden lg:flex gap-2" data-testid="store-featured">
              {featured.slice(0, 4).map(p => (
                <button key={p.product_id} onClick={() => setDetail(p)} className="w-24 h-24 rounded-xl overflow-hidden ring-1 ring-white/20">
                  <img src={p.photos[0].startsWith('http') ? p.photos[0] : api.publicFileUrl(p.photos[0])} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* MAIN */}
      <main className="max-w-7xl mx-auto px-4 py-6 grid lg:grid-cols-[240px_1fr] gap-6">
        <aside className={`${filtersOpen ? 'fixed inset-0 z-50 bg-black/40 lg:static lg:bg-transparent' : 'hidden'} lg:block`} data-testid="store-filters" onClick={(e) => { if (e.target === e.currentTarget) setFiltersOpen(false); }}>
          <div className="bg-white h-full w-72 lg:w-auto p-5 lg:p-0 lg:bg-transparent overflow-y-auto">
            <div className="flex items-center justify-between lg:hidden mb-4"><span className="font-bold">Filtros</span><button onClick={() => setFiltersOpen(false)}><X /></button></div>
            <div className="rounded-xl p-4" style={{ background: t.surface, border: `1px solid ${t.border}` }}>
              <p className="font-bold text-sm mb-3">Categorías</p>
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {catList.map(c => (
                  <button key={c.category_id || 'all'} onClick={() => { setActiveCat(c.category_id); setFiltersOpen(false); }} className="block w-full text-left px-3 py-2 rounded-lg text-sm font-medium" style={activeCat === c.category_id ? { background: t.primary, color: '#fff' } : { color: t.text }} data-testid={`store-cat-${c.category_id || 'all'}`}>{c.name}</button>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm mt-4 cursor-pointer" data-testid="store-onlystock"><input type="checkbox" checked={onlyStock} onChange={e => setOnlyStock(e.target.checked)} style={{ accentColor: t.primary }} /> Solo con stock</label>
            </div>
          </div>
        </aside>

        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold" style={{ color: t.muted }}>{filtered.length} resultado(s)</p>
            <button onClick={() => setFiltersOpen(true)} className="lg:hidden inline-flex items-center gap-1 text-sm font-semibold px-3 py-2 rounded-lg" style={{ border: `1px solid ${t.border}`, background: t.surface }} data-testid="store-filters-toggle"><SlidersHorizontal size={15} /> Filtros</button>
          </div>
          {/* Categorías deslizables (solo móvil) */}
          <div className="lg:hidden -mx-4 px-4 mb-5 overflow-x-auto no-scrollbar" data-testid="store-cat-scroll">
            <div className="flex gap-2 w-max pb-1">
              {catList.map(c => (
                <button
                  key={c.category_id || 'all'}
                  onClick={() => setActiveCat(c.category_id)}
                  className="whitespace-nowrap px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors"
                  style={activeCat === c.category_id ? { background: t.primary, color: '#fff', border: `1px solid ${t.primary}` } : { color: t.text, border: `1px solid ${t.border}`, background: t.surface }}
                  data-testid={`store-catchip-${c.category_id || 'all'}`}
                >{c.name}</button>
              ))}
            </div>
          </div>
          {Object.keys(grouped).length === 0 && <p className="text-center py-20" style={{ color: t.muted }} data-testid="store-empty">No hay productos que coincidan.</p>}
          <div className="space-y-10">
            {Object.entries(grouped).map(([catId, grp]) => {
              const isExp = expanded[catId];
              const show = isExp ? grp.items : grp.items.slice(0, 4);
              return (
                <section key={catId} data-testid={`store-dept-${catId}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2"><span className="inline-block h-6 w-1.5 rounded-full" style={{ background: t.primary }} /><h2 className="font-extrabold text-xl sm:text-2xl">{grp.name}</h2></div>
                    {grp.items.length > 4 && <button onClick={() => setExpanded(e => ({ ...e, [catId]: !isExp }))} className="inline-flex items-center gap-1 text-sm font-bold" style={{ color: t.primary }} data-testid={`store-expand-${catId}`}>{isExp ? 'Ver menos' : `Ver todos (${grp.items.length})`} <ChevronDown size={16} className={isExp ? 'rotate-180' : ''} /></button>}
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
                    {show.map(p => <ProductCard key={p.product_id} p={p} />)}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </main>

      <footer className="mt-6 py-8 px-4" style={{ background: t.headerBg, color: t.headerText }} data-testid="store-footer">
        <div className="max-w-7xl mx-auto text-center text-xs opacity-90 space-y-1">
          <p>© {new Date().getFullYear()} {data.company.name} · Powered by <span className="font-bold">SalesPRO</span></p>
          <p>Desarrollado y diseñado por <a href="https://www.7-fstudy.com" target="_blank" rel="noreferrer" className="font-semibold underline">www.7-fstudy.com</a></p>
        </div>
      </footer>

      {/* STICKY MOBILE BAR */}
      {cartCount > 0 && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 px-4 py-3 flex items-center justify-between shadow-[0_-6px_20px_rgba(0,0,0,.12)]" style={{ background: t.surface, borderTop: `1px solid ${t.border}` }} data-testid="store-sticky-bar">
          <div><p className="text-[11px]" style={{ color: t.muted }}>{cartCount} artículo(s)</p><p className="font-extrabold" style={{ color: t.primary }}>{money(cartTotal)}</p></div>
          <button onClick={() => setCartOpen(true)} className="px-6 py-2.5 rounded-full font-bold text-white" style={{ background: t.accent }} data-testid="store-sticky-checkout">Ver carrito</button>
        </div>
      )}

      {/* PRODUCT DETAIL MODAL */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="store-detail-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDetail(null)} />
          <div className="relative w-full max-w-3xl rounded-2xl overflow-hidden grid sm:grid-cols-2 st-pop" style={{ background: t.surface }}>
            <button onClick={() => setDetail(null)} className="absolute top-3 right-3 z-10 bg-white/90 rounded-full p-1.5" data-testid="store-detail-close"><X size={18} /></button>
            <div className="aspect-square bg-gray-100 flex items-center justify-center">
              {detail.photos && detail.photos[0] ? <img src={detail.photos[0].startsWith('http') ? detail.photos[0] : api.publicFileUrl(detail.photos[0])} alt={detail.name} className="w-full h-full object-cover" /> : <Package size={80} className="text-gray-300" />}
            </div>
            <div className="p-6 flex flex-col">
              {detail.marca && <span className="text-xs uppercase tracking-wide font-semibold" style={{ color: t.muted }}>{detail.marca}</span>}
              <h3 className="font-extrabold text-xl mt-1" style={{ color: t.text }}>{detail.name}</h3>
              {detail.code && <p className="text-sm mt-1" style={{ color: t.muted }}>Código: #{detail.code}</p>}
              <div className="mt-4 space-y-1 text-sm" style={{ color: t.text }}>
                {detail.unidad && <p><b>Unidad:</b> {detail.unidad}</p>}
                {detail.item_type !== 'servicio' && <p><b>Disponibilidad:</b> {(detail.stock || 0) > 0 ? `${detail.stock} en stock` : 'Sin stock'}</p>}
                {detail.description && <p className="pt-2" style={{ color: t.muted }}>{detail.description}</p>}
              </div>
              <div className="mt-auto pt-4">
                <p className="font-extrabold text-3xl" style={{ color: t.primary }}>{money(detail.price)}</p>
                <button onClick={() => addToCart(detail)} disabled={detail.item_type !== 'servicio' && (detail.stock || 0) <= 0} className="mt-3 w-full py-3 rounded-xl font-bold text-white disabled:opacity-40 flex items-center justify-center gap-2" style={{ background: t.accent }} data-testid="store-detail-add"><Plus size={18} /> Agregar al carrito</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CART DRAWER */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="store-cart-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCartOpen(false)} />
          <div className="relative w-full max-w-md h-full overflow-y-auto" style={{ background: t.surface }}>
            <div className="sticky top-0 flex items-center justify-between px-5 py-4" style={{ background: t.headerBg, color: t.headerText }}>
              <div className="flex items-center gap-2"><Brand cls="h-8 w-8 rounded-full" /><h3 className="font-extrabold text-lg">Tu carrito</h3></div>
              <button onClick={() => setCartOpen(false)} data-testid="store-cart-close"><X /></button>
            </div>
            <div className="p-5">
              {cartItems.length === 0 && <p className="py-10 text-center" style={{ color: t.muted }}>Tu carrito está vacío</p>}
              <div className="space-y-3">
                {cartItems.map(i => (
                  <div key={i.product_id} className="flex items-center gap-3 rounded-xl p-3" style={{ background: t.bg, border: `1px solid ${t.border}` }}>
                    <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate">{i.name}</p><p className="text-xs" style={{ color: t.muted }}>{money(i.price)} c/u</p></div>
                    <div className="flex items-center rounded-lg overflow-hidden" style={{ border: `1px solid ${t.border}` }}>
                      <button onClick={() => setQty(i.product_id, i.quantity - 1)} className="px-2 py-1" data-testid={`store-qty-minus-${i.product_id}`}><Minus size={14} /></button>
                      <span className="px-2 text-sm w-8 text-center">{i.quantity}</span>
                      <button onClick={() => setQty(i.product_id, i.quantity + 1)} className="px-2 py-1" data-testid={`store-qty-plus-${i.product_id}`}><Plus size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
              {cartItems.length > 0 && (
                <>
                  <div className="flex justify-between items-center mt-5 mb-4 text-lg font-bold"><span>Total</span><span style={{ color: t.primary }} data-testid="store-cart-total">{money(cartTotal)}</span></div>
                  {!isLogged && <div className="mb-3 p-3 rounded-lg text-sm" style={{ background: `${t.primary}12`, color: t.text }} data-testid="store-login-required">Para enviar tu pedido a la tienda debes <button onClick={() => navigate('/login')} className="font-bold underline" style={{ color: t.primary }}>iniciar sesión o registrarte</button>. También puedes pedir por WhatsApp sin registro.</div>}
                  <input value={customer.phone} onChange={e => setCustomer({ ...customer, phone: e.target.value })} placeholder="Tu teléfono (para WhatsApp)" className="w-full px-3 py-2.5 rounded-lg text-sm outline-none border mb-2" style={{ borderColor: t.border }} data-testid="store-customer-phone" />
                  <textarea value={customer.note} onChange={e => setCustomer({ ...customer, note: e.target.value })} placeholder="Nota (opcional)" rows={2} className="w-full px-3 py-2.5 rounded-lg text-sm outline-none border" style={{ borderColor: t.border }} data-testid="store-customer-note" />
                  <button onClick={orderWhatsapp} className="mt-3 w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 bg-[#25D366] text-white" data-testid="store-whatsapp-order"><MessageCircle size={18} /> Comprar por WhatsApp</button>
                  <button onClick={sendOrder} disabled={sending} className="mt-2 w-full py-3 rounded-lg font-bold text-white flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: isLogged ? t.primary : '#9CA3AF' }} data-testid="store-send-order"><Send size={17} /> {sending ? 'Enviando…' : (isLogged ? 'Enviar pedido a la tienda' : 'Inicia sesión para enviar')}</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
